import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

import KPICardHeader from './KPICardHeader.vue'
import KPICardBody from './KPICardBody.vue'
import KPICardFooter from './KPICardFooter.vue'
import { computed, inject, onUnmounted, ref, watch } from 'vue'
import { FormatOptions, formatter } from '../../formatter'
import { useDataSource } from '@/composables/useDataSources'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { KpiResult } from '@/api-client/models/indoorclimate/Contracts'
import useColoredEntities from '@/composables/useColoredEntities'
import { ColorScaleConfig, getColorForValue } from '@/utils/colorScale'
import { useI18n } from 'vue-i18n'
import FeatureComponent from '@/components/FeatureComponent.vue'
import md5 from 'md5'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { sharedDataKey } from '../../../types'
import { Feature } from '@/pinia/feature'

export interface Props {
  fullscreen: boolean
  title: string
  color?: string
  info?: string
  kpi?: number | string
  dataSource?: string
  colorScale?: ColorScaleConfig | string
  imgSrc?: string
  kpiFormat?: FormatOptions
  metadataFormat?: Record<string, FormatOptions>
  metadataLabels?: Record<string, LocaleString>
  numberOfSpaces?: number
  isLoading: boolean
  first?: boolean
  feature?: Feature
  kpiDetails?: {
    header: string[]
    rows: any[][]
  }
  showOnMap: boolean
  showOnMapEnabled: boolean
  kpiType: string
}

type Emits = {
  (e: 'toggleShowOnMap'): void
}

export interface GroupedData {
  value: number
  numberOfSpaces: number
  roomGroup: string | undefined
}


export default /*@__PURE__*/_defineComponent({
  __name: 'KPICard',
  props: {
    fullscreen: { type: Boolean },
    title: {},
    color: { default: undefined },
    info: { default: undefined },
    kpi: { default: undefined },
    dataSource: { default: undefined },
    colorScale: { default: undefined },
    imgSrc: { default: undefined },
    kpiFormat: { default: (): FormatOptions => ({}) },
    metadataFormat: { default: undefined },
    metadataLabels: { default: undefined },
    numberOfSpaces: { default: undefined },
    isLoading: { type: Boolean },
    first: { type: Boolean },
    feature: { default: undefined },
    kpiDetails: { default: undefined },
    showOnMap: { type: Boolean },
    showOnMapEnabled: { type: Boolean },
    kpiType: {}
  },
  emits: ["toggleShowOnMap"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emits = __emit

const { locale } = useI18n()

const filters = useCurrentFeatureFilters()

const presentInfo = ref<boolean>(false)
const presentDetails = ref<boolean>(false)

const colorScaleId = computed(() =>
  typeof props.colorScale === 'string' ? props.colorScale : undefined
)
const dataSourceId = computed(() => props.dataSource)
const sharedData = inject(sharedDataKey)
const fullscreen = computed(() => {
  return props.fullscreen || (sharedData?.fullscreen ?? false)
})

const dataSource = useDataSource<KpiResult[]>(dataSourceId)
const colorScaleDs = useDataSource<ColorScaleConfig>(colorScaleId)
const colorScale = computed<ColorScaleConfig | undefined>(() => {
  if (colorScaleDs.value?.data) return colorScaleDs.value.data
  if (typeof props.colorScale === 'object') return props.colorScale
  return undefined
})

const colorScaleColor = computed(() =>
  colorScale.value && typeof value.value === 'number'
    ? getColorForValue(colorScale.value, value.value).toHex()
    : '#000'
)

const color = computed(() => props.color ?? colorScaleColor.value)

const coloredEntities = useColoredEntities()

const value = computed(() => {
  let result = props.kpi ?? dataSource.value?.data?.[0]?.values?.[0]?.value
  if (props.kpiType === 'MAX_REMAINDER_CAPACITY' && props.numberOfSpaces === 1) {
    return undefined
  }
  if (result === null) {
    result = undefined
  }
  return result
})

const key = computed<string>(() => md5(props.title + props.feature?.name))

const showOnMapComplete = computed(
  () =>
    props.showOnMap ||
    (coloredEntities.currentKey !== undefined && coloredEntities.currentKey === key.value)
)

const showOnMapEnabledComplete = computed(() => {
  return props.showOnMapEnabled
})

const presentLegend = computed(() => {
  return [showOnMapComplete.value, showOnMapEnabledComplete.value, !fullscreen.value].every(
    (v) => v === true
  )
})

onUnmounted(() => {
  coloredEntities.reset()
})

watch(
  [dataSource, key],
  () => {
    if (props.first) {
      onToggleShowOnMap()
    }
  },
  { immediate: true }
)

function formatKpi(value: number | string) {
  if (typeof props.kpiFormat === 'function') {
    // return props.kpiFormat(value)
  }
  return formatter(value, locale.value, props.kpiFormat)
}

function onToggleShowOnMap() {
  if (
    dataSource.value &&
    dataSource.value.data &&
    colorScale.value &&
    props.colorScale &&
    filters.value?.submittedMeta.entityType !== undefined &&
    ['SPACE', 'LEVEL', 'REAL_ESTATE', 'COMPLEX', 'CONSTRUCTION'].includes(
      filters.value.submittedMeta.entityType
    )
  ) {
    const data = dataSource.value.data[1].values
      .map((v) => ({
        id: v.splitKey,
        meta: Object.fromEntries(
          Object.entries(v.numberOf)
            .map(([k, v]) => [
              props.metadataLabels?.[k.toLocaleLowerCase()]?.[locale.value],
              formatter(v, locale.value, props.metadataFormat?.[k])
            ])
            .filter(([k, v]) => k && v)
        ),
        value: v.value
      }))
      .filter(
        (v) => v.id !== undefined && v.id !== null && v.value !== undefined && v.value !== null
      )
    coloredEntities.reset()
    coloredEntities.set({
      key: key.value,
      data,
      colorScale: colorScale.value,
      title: props.title,
      unit: '',
      formatter: formatKpi
    })
  } else {
    coloredEntities.reset()
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: _normalizeClass(["data-visualization-kpi-card", { fullscreen: fullscreen.value }])
  }, [
    _createVNode(KPICardHeader, {
      title: _ctx.title,
      presentInfo: false,
      hasInfo: _ctx.info !== undefined,
      onOnChange: _cache[0] || (_cache[0] = (value) => (presentInfo.value = value))
    }, null, 8, ["title", "hasInfo"]),
    _createVNode(KPICardBody, {
      presentDetails: presentDetails.value,
      hasDetails: !!_ctx.kpiDetails,
      showOnMapEnabled: showOnMapEnabledComplete.value,
      showOnMap: showOnMapComplete.value,
      presentInfo: presentInfo.value,
      color: color.value,
      isLoading: _ctx.isLoading || (_unref(dataSource) !== undefined && _unref(dataSource).isLoading),
      kpi: value.value,
      imgSrc: _ctx.imgSrc,
      format: _ctx.kpiFormat,
      information: _ctx.info,
      onToggleDetails: _cache[1] || (_cache[1] = () => (presentDetails.value = !presentDetails.value)),
      onCloseInfo: _cache[2] || (_cache[2] = () => (presentInfo.value = false)),
      onToggleShowOnMap: _cache[3] || (_cache[3] = 
        () => {
          emits('toggleShowOnMap')
          if (_unref(dataSource) === undefined) onToggleShowOnMap()
        }
      )
    }, null, 8, ["presentDetails", "hasDetails", "showOnMapEnabled", "showOnMap", "presentInfo", "color", "isLoading", "kpi", "imgSrc", "format", "information"]),
    (_ctx.kpiDetails)
      ? (_openBlock(), _createBlock(KPICardFooter, {
          key: 0,
          presentDetails: presentDetails.value,
          header: _ctx.kpiDetails.header,
          rows: _ctx.kpiDetails.rows
        }, null, 8, ["presentDetails", "header", "rows"]))
      : _createCommentVNode("", true),
    (presentLegend.value)
      ? (_openBlock(), _createBlock(FeatureComponent, {
          key: 1,
          colorScale: colorScale.value,
          mean: value.value !== undefined && typeof value.value === 'number' ? value.value : 0,
          format: _ctx.kpiFormat,
          feature: "home/analytics/*/*/colorlegend",
          title: _ctx.title
        }, null, 8, ["colorScale", "mean", "format", "title"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})